import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserCheck,
  TrendingUp,
  Activity,
  Target,
  ClipboardCheck,
  FileText,
  BookOpen,
  MessageSquare,
  Scale,
  RefreshCw,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Building2,
  BarChart3,
  Briefcase,
  GraduationCap,
  History,
  Lightbulb,
  CheckCircle2,
  Clock,
  UserPlus,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from 'recharts';
import { adminService } from '@/services/api/adminService';
import type {
  UsageSummary,
  LoginActivity,
  FeatureAdoption,
  CampusBreakdown,
  FunctionalAreaBreakdown,
  AdoptionFunnel,
  OnboardingStats,
  ProfileCompletionStats,
} from '@/services/api/adminService';

// ==================== Sub-components ====================

interface KPICardProps {
  label: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: 'up' | 'down' | 'flat';
  trendValue?: string;
  accentColor?: string;
}

const KPICard: React.FC<KPICardProps> = ({
  label,
  value,
  subtitle,
  icon,
  trend,
  trendValue,
  accentColor = 'heritage-blue',
}) => {
  const trendIcon =
    trend === 'up' ? (
      <ArrowUpRight className="w-3 h-3 text-emerald-600" />
    ) : trend === 'down' ? (
      <ArrowDownRight className="w-3 h-3 text-red-500" />
    ) : (
      <Minus className="w-3 h-3 text-gray-400" />
    );

  const trendColor =
    trend === 'up'
      ? 'text-emerald-600'
      : trend === 'down'
      ? 'text-red-500'
      : 'text-gray-500';

  return (
    <Card className="p-5 hover:shadow-lg transition-shadow border-t-4 border-t-heritage-blue/20 hover:border-t-heritage-blue/60">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2.5 rounded-xl bg-${accentColor}/10`}>
          {icon}
        </div>
        {trend && trendValue && (
          <div className={`flex items-center gap-0.5 text-xs font-medium ${trendColor}`}>
            {trendIcon}
            {trendValue}
          </div>
        )}
      </div>
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5">{value}</p>
      {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
    </Card>
  );
};

// ==================== Adoption Funnel Bar ====================

interface FunnelBarProps {
  stages: { stage: string; count: number }[];
}

const FunnelBar: React.FC<FunnelBarProps> = ({ stages }) => {
  const maxCount = Math.max(...stages.map((s) => s.count), 1);
  const colors = ['#15305D', '#1e4a8a', '#29ABE2', '#7dd3fc'];

  return (
    <div className="space-y-3">
      {stages.map((stage, idx) => {
        const pct = maxCount > 0 ? (stage.count / maxCount) * 100 : 0;
        const dropOff =
          idx > 0 && stages[idx - 1].count > 0
            ? Math.round(
                ((stages[idx - 1].count - stage.count) / stages[idx - 1].count) * 100
              )
            : null;

        return (
          <div key={stage.stage}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-700">{stage.stage}</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-gray-900">
                  {stage.count.toLocaleString()}
                </span>
                {dropOff !== null && dropOff > 0 && (
                  <span className="text-xs text-red-400">-{dropOff}%</span>
                )}
              </div>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-7 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700 flex items-center justify-end pr-2"
                style={{
                  width: `${Math.max(pct, 4)}%`,
                  backgroundColor: colors[idx % colors.length],
                }}
              >
                {pct > 15 && (
                  <span className="text-xs font-medium text-white">
                    {Math.round(pct)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ==================== Login Activity Chart ====================

interface LoginChartProps {
  data: { date: string; login_count: number; unique_users: number }[];
  days: number;
  onDaysChange: (d: number) => void;
}

const LoginChart: React.FC<LoginChartProps> = ({ data, days, onDaysChange }) => {
  const formattedData = data.map((d) => ({
    ...d,
    label: new Date(d.date).toLocaleDateString('en-ZA', {
      day: 'numeric',
      month: 'short',
    }),
  }));

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-heritage-blue" />
            Login Activity
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">Daily logins and unique users</p>
        </div>
        <div className="flex bg-gray-100 rounded-lg p-0.5">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => onDaysChange(d)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                days === d
                  ? 'bg-white text-heritage-blue shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      {formattedData.length > 0 ? (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={formattedData}>
            <defs>
              <linearGradient id="loginGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#29ABE2" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#29ABE2" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="uniqueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#15305D" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#15305D" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9ca3af' }}
              tickLine={false}
              axisLine={false}
              width={40}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '12px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
              }}
            />
            <Area
              type="monotone"
              dataKey="login_count"
              stroke="#29ABE2"
              strokeWidth={2}
              fill="url(#loginGradient)"
              name="Total Logins"
            />
            <Area
              type="monotone"
              dataKey="unique_users"
              stroke="#15305D"
              strokeWidth={2}
              fill="url(#uniqueGradient)"
              name="Unique Users"
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[280px] flex items-center justify-center text-gray-400 text-sm">
          No login activity data available for this period
        </div>
      )}
    </Card>
  );
};

// ==================== Feature Adoption Cards ====================

const featureIconMap: Record<string, React.ReactNode> = {
  target: <Target className="w-5 h-5" />,
  'clipboard-check': <ClipboardCheck className="w-5 h-5" />,
  'file-text': <FileText className="w-5 h-5" />,
  'book-open': <BookOpen className="w-5 h-5" />,
  'message-square': <MessageSquare className="w-5 h-5" />,
  users: <Users className="w-5 h-5" />,
  scale: <Scale className="w-5 h-5" />,
};

interface FeatureGridProps {
  features: { name: string; count: number; total?: number; icon: string }[];
  period: string;
}

const FeatureGrid: React.FC<FeatureGridProps> = ({ features, period }) => {
  const barColors = [
    '#15305D',
    '#1a3d72',
    '#29ABE2',
    '#38bdf8',
    '#6366f1',
    '#8b5cf6',
    '#10b981',
  ];

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-heritage-blue" />
            Feature Adoption
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">Platform feature usage — {period}</p>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {features.map((feat, idx) => (
          <div
            key={feat.name}
            className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:shadow-sm transition-all"
          >
            <div
              className="p-2 rounded-lg text-white shrink-0"
              style={{ backgroundColor: barColors[idx % barColors.length] }}
            >
              {featureIconMap[feat.icon] || <Activity className="w-5 h-5" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 truncate">{feat.name}</p>
              <p className="text-lg font-bold text-gray-900">{feat.count.toLocaleString()}</p>
              {feat.total !== undefined && feat.total > 0 && (
                <p className="text-xs text-gray-400">
                  of {feat.total.toLocaleString()} total
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ==================== Campus Table ====================

interface CampusTableProps {
  campuses: {
    campus: string;
    total_users: number;
    active_users: number;
    adoption_rate: number;
  }[];
}

const CampusTable: React.FC<CampusTableProps> = ({ campuses }) => {
  const getRateColor = (rate: number) => {
    if (rate >= 80) return 'bg-emerald-500';
    if (rate >= 50) return 'bg-amber-400';
    return 'bg-red-400';
  };

  const getRateBadge = (rate: number) => {
    if (rate >= 80) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (rate >= 50) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  return campuses.length > 0 ? (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Campus
            </th>
            <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total
            </th>
            <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Active
            </th>
            <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider w-48">
              Adoption
            </th>
            <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Rate
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {campuses.map((c, i) => (
            // Combine campus + index because the backend can emit
            // multiple rows with campus="Unknown" (one per legal entity
            // / null-campus bucket). React requires unique keys.
            <tr key={`${c.campus}-${i}`} className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-2 font-medium text-gray-900">{c.campus}</td>
              <td className="py-3 px-2 text-right text-gray-600">
                {c.total_users.toLocaleString()}
              </td>
              <td className="py-3 px-2 text-right text-gray-600">
                {c.active_users.toLocaleString()}
              </td>
              <td className="py-3 px-2">
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${getRateColor(
                      c.adoption_rate
                    )}`}
                    style={{ width: `${Math.min(c.adoption_rate, 100)}%` }}
                  />
                </div>
              </td>
              <td className="py-3 px-2 text-right">
                <span
                  className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${getRateBadge(
                    c.adoption_rate
                  )}`}
                >
                  {c.adoption_rate}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="py-12 text-center text-gray-400 text-sm">
      No campus data available
    </div>
  );
};

// ==================== Functional Area Table ====================

interface FunctionalAreaTableProps {
  areas: {
    functional_area: string;
    total_users: number;
    active_users: number;
    adoption_rate: number;
  }[];
}

const FunctionalAreaTable: React.FC<FunctionalAreaTableProps> = ({ areas }) => {
  const getRateColor = (rate: number) => {
    if (rate >= 80) return 'bg-emerald-500';
    if (rate >= 50) return 'bg-amber-400';
    return 'bg-red-400';
  };

  const getRateBadge = (rate: number) => {
    if (rate >= 80) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (rate >= 50) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  return areas.length > 0 ? (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Functional Area
            </th>
            <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Total
            </th>
            <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Active
            </th>
            <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider w-48">
              Adoption
            </th>
            <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
              Rate
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {areas.map((a, i) => (
            <tr key={`${a.functional_area}-${i}`} className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-2 font-medium text-gray-900">{a.functional_area}</td>
              <td className="py-3 px-2 text-right text-gray-600">
                {a.total_users.toLocaleString()}
              </td>
              <td className="py-3 px-2 text-right text-gray-600">
                {a.active_users.toLocaleString()}
              </td>
              <td className="py-3 px-2">
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${getRateColor(
                      a.adoption_rate
                    )}`}
                    style={{ width: `${Math.min(a.adoption_rate, 100)}%` }}
                  />
                </div>
              </td>
              <td className="py-3 px-2 text-right">
                <span
                  className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full border ${getRateBadge(
                    a.adoption_rate
                  )}`}
                >
                  {a.adoption_rate}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div className="py-12 text-center text-gray-400 text-sm">
      No functional area data available — run an employee sync to populate
    </div>
  );
};

// ==================== Main Dashboard ====================

const UsageAdoptionDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loginDays, setLoginDays] = useState(30);

  const [summary, setSummary] = useState<UsageSummary | null>(null);
  const [loginActivity, setLoginActivity] = useState<LoginActivity | null>(null);
  const [featureAdoption, setFeatureAdoption] = useState<FeatureAdoption | null>(null);
  const [campusBreakdown, setCampusBreakdown] = useState<CampusBreakdown | null>(null);
  const [functionalAreaBreakdown, setFunctionalAreaBreakdown] = useState<FunctionalAreaBreakdown | null>(null);
  const [adoptionFunnel, setAdoptionFunnel] = useState<AdoptionFunnel | null>(null);
  const [onboardingStats, setOnboardingStats] = useState<OnboardingStats | null>(null);
  const [profileStats, setProfileStats] = useState<ProfileCompletionStats | null>(null);
  const [breakdownTab, setBreakdownTab] = useState<'campus' | 'functional-area'>('campus');
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchAll = useCallback(
    async (isRefresh = false) => {
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      try {
        const [summaryRes, loginRes, featureRes, campusRes, faRes, funnelRes, onboardingRes, profileRes] =
          await Promise.allSettled([
            adminService.getUsageSummary(),
            adminService.getLoginActivity(loginDays),
            adminService.getFeatureAdoption(),
            adminService.getCampusBreakdown(),
            adminService.getFunctionalAreaBreakdown(),
            adminService.getAdoptionFunnel(),
            adminService.getOnboardingStats(),
            adminService.getProfileCompletionStats(),
          ]);

        if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
        if (loginRes.status === 'fulfilled') setLoginActivity(loginRes.value);
        if (featureRes.status === 'fulfilled') setFeatureAdoption(featureRes.value);
        if (campusRes.status === 'fulfilled') setCampusBreakdown(campusRes.value);
        if (faRes.status === 'fulfilled') setFunctionalAreaBreakdown(faRes.value);
        if (funnelRes.status === 'fulfilled') setAdoptionFunnel(funnelRes.value);
        if (onboardingRes.status === 'fulfilled') setOnboardingStats(onboardingRes.value);
        if (profileRes.status === 'fulfilled') setProfileStats(profileRes.value);

        setLastRefresh(new Date());
      } catch (err) {
        console.error('Error fetching usage analytics:', err);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [loginDays]
  );

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Refetch login activity when days change
  useEffect(() => {
    const refetchLogin = async () => {
      try {
        const res = await adminService.getLoginActivity(loginDays);
        setLoginActivity(res);
      } catch (err) {
        console.error('Error refetching login activity:', err);
      }
    };
    if (!loading) refetchLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginDays]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 bg-gray-100 rounded-xl" />
          ))}
        </div>
        <div className="h-80 bg-gray-100 rounded-xl" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64 bg-gray-100 rounded-xl" />
          <div className="h-64 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Usage & Adoption Analytics</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Real-time platform engagement metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-400">
            Updated {lastRefresh.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <button
            onClick={() => fetchAll(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-heritage-blue border border-heritage-blue/30 rounded-lg hover:bg-heritage-blue/5 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KPICard
          label="Total Users"
          value={summary?.total_users?.toLocaleString() || '0'}
          subtitle="Registered & active"
          icon={<Users className="w-5 h-5 text-heritage-blue" />}
        />
        <KPICard
          label="Active (30d)"
          value={summary?.active_30d?.toLocaleString() || '0'}
          subtitle="Logged in last 30 days"
          icon={<UserCheck className="w-5 h-5 text-emerald-600" />}
          trend={summary && summary.active_30d > 0 ? 'up' : 'flat'}
          accentColor="emerald-600"
        />
        <KPICard
          label="DAU"
          value={summary?.dau?.toLocaleString() || '0'}
          subtitle="Active today"
          icon={<Activity className="w-5 h-5 text-future-blue" />}
          accentColor="future-blue"
        />
        <KPICard
          label="WAU"
          value={summary?.wau?.toLocaleString() || '0'}
          subtitle="Active this week"
          icon={<Calendar className="w-5 h-5 text-indigo-600" />}
          accentColor="indigo-600"
        />
        <KPICard
          label="New This Month"
          value={summary?.new_users_month?.toLocaleString() || '0'}
          subtitle="New registrations"
          icon={<TrendingUp className="w-5 h-5 text-violet-600" />}
          accentColor="violet-600"
        />
        <KPICard
          label="Adoption Rate"
          value={`${summary?.adoption_rate || 0}%`}
          subtitle="30-day active / total"
          icon={<BarChart3 className="w-5 h-5 text-heritage-blue" />}
          trend={
            summary
              ? summary.adoption_rate >= 70
                ? 'up'
                : summary.adoption_rate >= 40
                ? 'flat'
                : 'down'
              : 'flat'
          }
          trendValue={
            summary
              ? summary.adoption_rate >= 70
                ? 'Healthy'
                : summary.adoption_rate >= 40
                ? 'Growing'
                : 'Needs attention'
              : ''
          }
        />
      </div>

      {/* Login Activity Chart */}
      <LoginChart
        data={loginActivity?.data || []}
        days={loginDays}
        onDaysChange={setLoginDays}
      />

      {/* Feature Adoption + Adoption Funnel */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <FeatureGrid
            features={featureAdoption?.features || []}
            period={featureAdoption?.period || new Date().getFullYear().toString()}
          />
        </div>
        <div>
          <Card className="p-6 h-full">
            <div className="mb-5">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-heritage-blue" />
                Adoption Funnel
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                User journey progression
              </p>
            </div>
            <FunnelBar stages={adoptionFunnel?.funnel || []} />
          </Card>
        </div>
      </div>

      {/* Onboarding & Profile Completion */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Onboarding Progress */}
        <Card className="p-6">
          <div className="mb-5">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-heritage-blue" />
              Onboarding Progress
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">User onboarding stage distribution</p>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
              <p className="text-xs text-emerald-600 font-medium">Completion Rate</p>
              <p className="text-xl font-bold text-emerald-700">{onboardingStats?.completion_rate ?? 0}%</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-xs text-blue-600 font-medium">Duration (Median)</p>
              <p className="text-xl font-bold text-blue-700">
                {(() => {
                  const m = onboardingStats?.median_duration_minutes ?? 0;
                  if (m >= 1440) return `${Math.round(m / 1440)}d`;
                  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
                  return `${m}m`;
                })()}
              </p>
              <p className="text-[10px] text-blue-400 mt-0.5">
                Avg: {(() => {
                  const a = onboardingStats?.avg_duration_minutes ?? 0;
                  if (a >= 1440) return `~${Math.round(a / 1440)}d`;
                  if (a >= 60) return `${Math.floor(a / 60)}h ${a % 60}m`;
                  return `${a}m`;
                })()}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-violet-50 border border-violet-100">
              <p className="text-xs text-violet-600 font-medium">Coverage</p>
              <p className="text-xl font-bold text-violet-700">
                {onboardingStats && onboardingStats.total_users > 0
                  ? Math.round((onboardingStats.users_with_onboarding / onboardingStats.total_users) * 100)
                  : 0}%
              </p>
            </div>
          </div>

          {/* Stage distribution bar chart */}
          {onboardingStats && Object.keys(onboardingStats.stage_counts).length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                layout="vertical"
                data={[
                  { stage: 'Not Started', count: onboardingStats.stage_counts['not_started'] || 0 },
                  { stage: 'Welcome', count: onboardingStats.stage_counts['welcome'] || 0 },
                  { stage: 'F&O Confirm', count: onboardingStats.stage_counts['fo_confirmation'] || 0 },
                  { stage: 'Profile Setup', count: onboardingStats.stage_counts['profile_setup'] || 0 },
                  { stage: 'Platform Tour', count: onboardingStats.stage_counts['platform_tour'] || 0 },
                  { stage: 'Completed', count: onboardingStats.stage_counts['completed'] || 0 },
                ]}
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} />
                <YAxis dataKey="stage" type="category" tick={{ fontSize: 11, fill: '#6b7280' }} width={90} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(value: number) => [value.toLocaleString(), 'Users']}
                />
                <Bar dataKey="count" radius={[0, 4, 4, 0]} maxBarSize={24}>
                  {[
                    { stage: 'Not Started', count: onboardingStats.stage_counts['not_started'] || 0 },
                    { stage: 'Welcome', count: onboardingStats.stage_counts['welcome'] || 0 },
                    { stage: 'F&O Confirm', count: onboardingStats.stage_counts['fo_confirmation'] || 0 },
                    { stage: 'Profile Setup', count: onboardingStats.stage_counts['profile_setup'] || 0 },
                    { stage: 'Platform Tour', count: onboardingStats.stage_counts['platform_tour'] || 0 },
                    { stage: 'Completed', count: onboardingStats.stage_counts['completed'] || 0 },
                  ].map((_, idx) => (
                    <Cell key={idx} fill={idx === 5 ? '#10b981' : idx === 0 ? '#d1d5db' : '#29ABE2'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[220px] flex items-center justify-center text-gray-400 text-sm">
              No onboarding data available
            </div>
          )}

          {/* Scenario breakdown */}
          {onboardingStats && Object.keys(onboardingStats.scenario_breakdown).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">By Scenario</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(onboardingStats.scenario_breakdown).map(([scenario, count]) => (
                  <span key={scenario} className="text-xs px-2.5 py-1 bg-gray-100 rounded-full text-gray-600">
                    {scenario.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}: <strong>{count}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Profile Completion */}
        <Card className="p-6">
          <div className="mb-5">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-heritage-blue" />
              Professional Profile Completion
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">Users who have filled in professional details</p>
          </div>

          {/* Section progress cards */}
          <div className="space-y-4 mb-5">
            {[
              {
                label: 'Qualifications',
                count: profileStats?.qualifications_completed ?? 0,
                total: profileStats?.total_users ?? 0,
                icon: <GraduationCap className="w-4 h-4" />,
                color: 'emerald',
              },
              {
                label: 'Work History',
                count: profileStats?.work_history_completed ?? 0,
                total: profileStats?.total_users ?? 0,
                icon: <History className="w-4 h-4" />,
                color: 'blue',
              },
              {
                label: 'Expertise',
                count: profileStats?.expertise_completed ?? 0,
                total: profileStats?.total_users ?? 0,
                icon: <Lightbulb className="w-4 h-4" />,
                color: 'violet',
              },
            ].map((section) => {
              const pct = section.total > 0 ? Math.round((section.count / section.total) * 100) : 0;
              const colorMap: Record<string, { bg: string; fill: string; text: string; icon: string }> = {
                emerald: { bg: 'bg-emerald-100', fill: 'bg-emerald-500', text: 'text-emerald-700', icon: 'text-emerald-600' },
                blue: { bg: 'bg-blue-100', fill: 'bg-blue-500', text: 'text-blue-700', icon: 'text-blue-600' },
                violet: { bg: 'bg-violet-100', fill: 'bg-violet-500', text: 'text-violet-700', icon: 'text-violet-600' },
              };
              const c = colorMap[section.color];

              return (
                <div key={section.label} className="flex items-center gap-3">
                  <div className={`p-1.5 rounded-lg ${c.bg} ${c.icon}`}>
                    {section.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700">{section.label}</span>
                      <span className="text-sm font-bold text-gray-900">
                        {section.count.toLocaleString()} <span className="text-xs font-normal text-gray-400">/ {section.total.toLocaleString()}</span>
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2.5">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${c.fill}`}
                        style={{ width: `${Math.min(pct, 100)}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">{pct}% of users</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Completion distribution chart */}
          {profileStats && profileStats.completion_distribution && (
            <>
              <div className="pt-4 border-t border-gray-100 mb-3">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Profile Sections Completed</p>
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart
                  data={[
                    { range: 'No Sections', count: profileStats.completion_distribution['0%'] || 0 },
                    { range: '1 Section', count: profileStats.completion_distribution['33%'] || 0 },
                    { range: '2 Sections', count: profileStats.completion_distribution['67%'] || 0 },
                    { range: '3 Sections', count: profileStats.completion_distribution['100%'] || 0 },
                  ]}
                  margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                  <XAxis dataKey="range" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={45} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '12px' }}
                    formatter={(value: number) => [value.toLocaleString(), 'Users']}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={48}>
                    <Cell fill="#fca5a5" />
                    <Cell fill="#fcd34d" />
                    <Cell fill="#93c5fd" />
                    <Cell fill="#6ee7b7" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </>
          )}

          {/* Summary stat */}
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-sm text-gray-600">All sections complete</span>
            <span className="text-lg font-bold text-emerald-600">
              {profileStats?.all_sections_completed ?? 0} <span className="text-xs font-normal text-gray-400">users</span>
            </span>
          </div>
        </Card>
      </div>

      {/* Campus & Functional Area Breakdown */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              {breakdownTab === 'campus' ? (
                <Building2 className="w-5 h-5 text-heritage-blue" />
              ) : (
                <Briefcase className="w-5 h-5 text-heritage-blue" />
              )}
              {breakdownTab === 'campus' ? 'Campus Engagement' : 'Functional Area Engagement'}
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Adoption rate by {breakdownTab === 'campus' ? 'campus' : 'functional area'} (last 30 days)
            </p>
          </div>
          <div className="flex rounded-lg border border-gray-200 overflow-hidden">
            <button
              onClick={() => setBreakdownTab('campus')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                breakdownTab === 'campus'
                  ? 'bg-heritage-blue text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Campus
            </button>
            <button
              onClick={() => setBreakdownTab('functional-area')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors border-l border-gray-200 ${
                breakdownTab === 'functional-area'
                  ? 'bg-heritage-blue text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              Functional Area
            </button>
          </div>
        </div>
        {breakdownTab === 'campus' ? (
          <CampusTable campuses={campusBreakdown?.campuses || []} />
        ) : (
          <FunctionalAreaTable areas={functionalAreaBreakdown?.functional_areas || []} />
        )}
      </Card>
    </div>
  );
};

export default UsageAdoptionDashboard;
